import { format, parse } from "date-fns"
import config from "../../config.js"

const LocalityIds = "13001|13100|13290|13090|13540|13080|13109|13770|13920|13921|13095|13113|13510|13032"
const OperatorIds = "7"
const STOP_PLACE = "4"
const ADDRESS = "3"
const POINT_OF_INTEREST = "1"
const ITINERARY_URL = "https://api.pilote4.cityway.fr/journeyplanner/api/opt/PlanTrips/json?"

export function getTripPointsFromApi(query: string) {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
  }
  const url = "https://tsvc.pilote3.cityway.fr:443/api/transport/v3/trippoint/GetTripPoints/json?"
  const params =
    "Keywords=" +
    query.split(" ").join("%20") +
    `&OperatorIds=${OperatorIds}` +
    `&LocalityIds=${LocalityIds}&Lang=${config.locale}`

  return fetch(url + params, requestOptions).then(async response => {
    const data = await response.json()
    if (!response.ok) {
      const error = (data && data.message) || response.statusText
      return Promise.reject(error)
    }
    return data.Data
  })
}

export function getStopsFromApi(query: string) {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
  }
  const url = "https://tsvc.pilote3.cityway.fr:443/api/transport/v3/stop/SearchStops/json?"
  const params =
    "Keywords=" +
    query.split(" ").join("%20") +
    `&OperatorIds=${OperatorIds}` +
    `&LocalityIds=${LocalityIds}&Lang=${config.locale}`
  return fetch(url + params, requestOptions).then(async response => {
    const data = await response.json()
    if (!response.ok) {
      const error = (data && data.message) || response.statusText
      return Promise.reject(error)
    }
    return data.Data
  })
}

export function getNextTrips(logicalStopId: number) {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
    },
  }
  const url = "https://tsvc.pilote3.cityway.fr:443/api/transport/v3/timetable/GetNextStopHours/json?"
  const params =
    "LogicalStopIds=" + logicalStopId + `&OperatorIds=${OperatorIds}&Lang=${config.locale}` + "&MaxItemsByLine=2"
  return fetch(url + params, requestOptions).then(async response => {
    const data = await response.json()
    if (!response.ok) {
      const error = (data && data.message) || response.statusText
      return Promise.reject(error)
    }
    return data.Data
  })
}

export type ItineraryPoint = string | { value?: string; latitude?: string; longitude?: string }

export type TripCoordinates = number[][]

export type TripSegment = {
  walkSegmentCoordinates?: TripCoordinates
  walkSegmentDetails?: Record<string, unknown>
  rideSegmentCoordinates?: TripCoordinates
  rideSegmentDetails?: Record<string, unknown>
  lineIcon?: Record<string, unknown>[] | null
}

export type Trip = {
  tripType?: string
  TripTitle?: string
  sections: TripSegment[][]
  fullTripData?: Record<string, unknown>
}

export function getItineraryLePilote(
  departureStop: ItineraryPoint,
  arrivalStop: ItineraryPoint,
  date: string,
  time: string,
  departurePointType: string,
  arrivalPointType: string,
  dateType: boolean
) {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  }
  const dateString = date + time
  const newDate = parse(dateString, "dd/MM/yyyyHH:mm", new Date())
  const formatedDate = format(newDate, "yyyy-MM-dd'T'HH:mm")
  const dateTypeValue = dateType ? "Arrival" : "Departure"

  const departureType =
    departurePointType == STOP_PLACE
      ? "STOP_PLACE"
      : departurePointType == "COORDINATES"
        ? "COORDINATES"
        : departurePointType == ADDRESS
          ? "ADDRESS"
          : departurePointType == POINT_OF_INTEREST
            ? "POI"
            : ""

  const arrivalType =
    arrivalPointType == STOP_PLACE
      ? "STOP_PLACE"
      : arrivalPointType == "COORDINATES"
        ? "COORDINATES"
        : arrivalPointType == ADDRESS
          ? "ADDRESS"
          : arrivalPointType == POINT_OF_INTEREST
            ? "POI"
            : ""

  let departure: string
  let arrival: string
  if (departureType === "COORDINATES" && typeof departureStop === "object") {
    departure =
      "DepartureType=" +
      departureType +
      "&DepartureLatitude=" +
      departureStop.latitude +
      "&DepartureLongitude=" +
      departureStop.longitude
  } else {
    const departureValue = typeof departureStop === "object" ? departureStop.value : departureStop
    departure = "DepartureType=" + departureType + `&DepartureId=` + departureValue
  }

  if (arrivalType === "COORDINATES" && typeof arrivalStop === "object") {
    arrival =
      "&ArrivalType=" +
      arrivalType +
      "&ArrivalLatitude=" +
      arrivalStop.latitude +
      "&ArrivalLongitude=" +
      arrivalStop.longitude
  } else {
    const arrivalValue = typeof arrivalStop === "object" ? arrivalStop.value : arrivalStop
    arrival = "&ArrivalType=" + arrivalType + `&ArrivalId=` + arrivalValue
  }
  const params =
    departure + arrival + "&Date=" + formatedDate + "&DateType=" + dateTypeValue + "&AllowedOperatorIds=" + OperatorIds
  return fetch(ITINERARY_URL + params, requestOptions).then(async response => {
    if (!response.ok) {
      const error = response.statusText
      return Promise.reject(error)
    }
    const data = await response.json()
    if (response.ok && data && data.StatusCode != 200) {
      const error = [data.StatusCode, data.Data, data.Message]
      return Promise.reject(error)
    }
    return Promise.resolve(data.Data)
  })
}

function getLineIcon(lineCode, linesIconsData) {
  const lineCodeLowercase = lineCode.toLowerCase()
  return linesIconsData.filter(x => x.basename.endsWith(`${lineCodeLowercase}`))
}

export function getItineraryLePiloteFormatedResults(tripsData, linesIconsData) {
  if (tripsData) {
    if (tripsData[0].response.Status.Code == "OK") {
      const trips = tripsData[0].response.trips.Trip.map(trip => {
        const sections = trip.sections.Section.map(section => {
          const allTripSections = []
          if (section.Leg) {
            const segment = getItineraryWalkingSegments(section)
            if (segment) {
              const mergedSegment = [].concat.apply([], segment)
              allTripSections.push({
                walkSegmentCoordinates: mergedSegment,
                walkSegmentDetails: section.Leg,
              })
            }
          }
          if (section.PTRide) {
            const segment = getItinerarySegments(section)
            if (segment) {
              const mergedSegment = [].concat.apply([], segment)
              if (linesIconsData) {
                const iconData = getLineIcon(section.PTRide.Line.Number, linesIconsData)
                allTripSections.push({
                  rideSegmentCoordinates: mergedSegment,
                  rideSegmentDetails: section.PTRide,
                  lineIcon: iconData,
                })
              } else {
                allTripSections.push({
                  rideSegmentCoordinates: mergedSegment,
                  rideSegmentDetails: section.PTRide,
                  lineIcon: null,
                })
              }
            }
          }
          return allTripSections
        })
        const tripTitle = trip.TripTitle
        return { tripType: tripTitle, sections: sections, fullTripData: trip }
      })
      return trips
    }
  }
}

export function getItinerarySegments(section) {
  const segments = []
  if (section.PTRide) {
    let positions
    section.PTRide.steps.Step.map(x => {
      const step = x.Geometry.split("(").pop().split(")")[0]
      positions = step.split(",")
      const stepPositions = positions.map(pos => {
        const cleanCoordinates = pos.trim().split(" ")
        return cleanCoordinates.map(y => {
          return parseFloat(y)
        })
      })
      if (stepPositions) {
        segments.push(stepPositions)
      }
    })
    return segments
  }
  return null
}

export function getItineraryWalkingSegments(section) {
  let segments = []
  if (section.Leg) {
    let positions
    section.Leg.pathLinks.PathLink.map(walkSegment => {
      if (walkSegment.Geometry) {
        const step = walkSegment.Geometry.split("(").pop().split(")")[0]
        positions = step.split(",")

        const stepPositions = positions.map(position => {
          const cleanCoordinates = position.trim().split(" ")
          return cleanCoordinates.map(coordinates => {
            return parseFloat(coordinates)
          })
        })
        if (stepPositions) {
          segments.push(stepPositions)
        }
      } else {
        segments = [
          [walkSegment.Arrival.Site.Position.Lat, walkSegment.Arrival.Site.Position.Long],
          [walkSegment.Departure.Site.Position.Lat, walkSegment.Departure.Site.Position.Long],
        ]
      }
    })
    return segments
  }
  return null
}
